import { useState, useEffect } from 'react';
import api from '../services/api';
import { FieldDefinition } from '../contexts/FieldDefinitionsContext';

/**
 * Loads the quote filter field registry (GET /field-definitions?scope=quotes) for
 * the order-management/quotes Custom Filter. Kept separate from the global
 * FieldDefinitionsContext (which is orders-scoped) so quote fields never leak
 * into the Dispatch/LC pickers. Module-cached so reopening the dialog is instant.
 */
let cache: FieldDefinition[] | null = null;

export function useQuoteFieldDefinitions(enabled: boolean = true) {
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinition[]>(cache || []);
  const [loading, setLoading] = useState(!cache);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    // Cache already warmed (e.g. by the filter dialog) — sync it into local state.
    // Previously this branch returned without setFieldDefinitions, so a consumer
    // mounted before the cache filled (the page banner) kept an empty list and
    // fell back to raw fieldnames instead of display names.
    if (cache) {
      setFieldDefinitions(cache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get('/field-definitions', { params: { scope: 'quotes' } })
      .then((resp) => {
        if (cancelled) return;
        const defs = (resp.data?.data || []) as FieldDefinition[];
        cache = defs;
        setFieldDefinitions(defs);
      })
      .catch((e) => {
        if (!cancelled) setError(e as Error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { fieldDefinitions, loading, error };
}

export default useQuoteFieldDefinitions;
